import { useEffect, useRef, useState } from 'react';
import { ARTIST, LINKS } from '../config';
import { mainImage } from '../photos';
import swatches from '../assets/swatches.png';
import Wordmark from '../components/Wordmark';
import ScatterWords from '../components/ScatterWords';
import { ArrowUpRight } from '../components/Icons';

const SOCIALS = [
  ['spotify', 'Spotify'],
  ['instagram', 'Instagram'],
  ['youtube', 'YouTube'],
];

// Shown instead of the bouncing name when the photo is clicked.
const WORDS = [
  ['late', 'left-[46%] top-[6%] text-7xl opacity-40 md:text-9xl'],
  ['small rooms', 'left-[6%] top-[12%] text-2xl md:text-4xl'],
  ['fan on high', 'right-[8%] top-[22%] text-lg md:text-2xl'],
  ['(too loud)', 'left-[38%] top-[45%] text-base md:text-xl'],
  ['hello?', 'right-[28%] top-[62%] text-xl md:text-2xl'],
  ['tape hiss', 'bottom-[24%] right-[10%] text-3xl md:text-5xl'],
  ['no. 7', 'bottom-[14%] left-[7%] text-sm tracking-wide'],
];

// Like the old DVD screensaver, the logo changes colour on every bounce.
// Brand colours that stay readable over the photo.
const BOUNCE_COLOURS = ['#ede5d6', '#c08a2e', '#bb9669'];

// Moves `logoRef` around inside `fieldRef`, bouncing off the edges.
function useDvdBounce(fieldRef, logoRef) {
  useEffect(() => {
    const field = fieldRef.current;
    const logo = logoRef.current;
    if (!field || !logo || typeof window.requestAnimationFrame !== 'function') return;

    // The capitals stop ~0.05em above the bottom of their line box (measured for
    // Archivo Black at leading 0.8), so let the box sink that far and the letters
    // themselves touch the bottom edge.
    const room = () => {
      const fontSize = parseFloat(window.getComputedStyle(logo.firstElementChild).fontSize) || 0;
      return {
        w: Math.max(field.clientWidth - logo.offsetWidth, 0),
        h: Math.max(field.clientHeight - logo.offsetHeight + fontSize * 0.05, 0),
      };
    };
    const place = (x, y) => {
      logo.style.transform = `translate3d(${x}px, ${y}px, 0)`;
    };

    const start = room();
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      place(start.w / 2, start.h / 2);
      return;
    }

    let x = Math.random() * start.w;
    let y = Math.random() * start.h;
    let vx = Math.random() < 0.5 ? -1 : 1;
    let vy = Math.random() < 0.5 ? -1 : 1;
    let colour = 0;
    let last = null;
    let frame;

    const tick = (now) => {
      // Cap the step so a backgrounded tab doesn't make the logo jump.
      const dt = last === null ? 0 : Math.min((now - last) / 1000, 0.05);
      last = now;

      const { w, h } = room();
      const speed = Math.max(60, field.clientWidth * 0.06); // px per second
      x += vx * speed * dt;
      y += vy * speed * dt;

      let bounced = false;
      if (w > 0 && (x <= 0 || x >= w)) {
        x = Math.min(Math.max(x, 0), w);
        vx = x <= 0 ? 1 : -1;
        bounced = true;
      }
      if (h > 0 && (y <= 0 || y >= h)) {
        y = Math.min(Math.max(y, 0), h);
        vy = y <= 0 ? 1 : -1;
        bounced = true;
      }
      if (bounced) {
        colour = (colour + 1) % BOUNCE_COLOURS.length;
        logo.style.color = BOUNCE_COLOURS[colour];
      }

      place(Math.min(x, w), Math.min(y, h));
      frame = window.requestAnimationFrame(tick);
    };

    // Coming back to the tab: pick up where it left off instead of stalling or jumping.
    const resume = () => {
      if (document.visibilityState !== 'visible') return;
      window.cancelAnimationFrame(frame);
      last = null;
      frame = window.requestAnimationFrame(tick);
    };

    frame = window.requestAnimationFrame(tick);
    document.addEventListener('visibilitychange', resume);
    window.addEventListener('pageshow', resume);
    return () => {
      window.cancelAnimationFrame(frame);
      document.removeEventListener('visibilitychange', resume);
      window.removeEventListener('pageshow', resume);
    };
  }, [fieldRef, logoRef]);
}

export default function Home() {
  const fieldRef = useRef(null);
  const logoRef = useRef(null);
  useDvdBounce(fieldRef, logoRef);
  // Clicking the photo swaps the bouncing name for the words, and back again.
  // The name keeps bouncing while hidden, so it reappears somewhere new.
  const [showWords, setShowWords] = useState(false);

  return (
    <main className="relative flex min-h-[560px] flex-1 flex-col overflow-hidden bg-black text-bone">
      <img
        src={mainImage}
        alt={`${ARTIST} playing guitar`}
        className="absolute inset-0 h-full w-full object-cover object-[35%_75%]"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-ink/60 via-transparent to-ink/20" />
      <ScatterWords words={WORDS} hidden={!showWords} />
      <button
        type="button"
        onClick={() => setShowWords((v) => !v)}
        aria-pressed={showWords}
        aria-label={showWords ? 'Show the name' : 'Show the words'}
        className="absolute inset-0 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-mustard/60"
      />

      {/* The area the wordmark bounces around in: edge to edge, between the nav and the bottom bar. */}
      <div ref={fieldRef} className="pointer-events-none relative flex-1">
        <div
          ref={logoRef}
          className={`absolute left-0 top-0 text-bone transition-[color,opacity] duration-300 will-change-transform ${
            showWords ? 'opacity-0' : 'opacity-100'
          }`}
        >
          <Wordmark
            as="h1"
            className="whitespace-nowrap text-[11vw] leading-[0.8] tracking-tighter drop-shadow-[0_2px_24px_rgba(0,0,0,0.35)] md:text-[6vw]"
          />
        </div>
      </div>

      {/* Bottom bar with the links. */}
      <div className="relative flex flex-col items-center gap-3 bg-black px-5 py-4 md:flex-row md:justify-between md:px-10">
        {/* On mobile the links come first and the swatches sit underneath them. */}
        <img src={swatches} alt="" className="order-2 w-14 md:order-none" />
        <ul className="order-1 md:order-none flex flex-wrap justify-center gap-x-6 gap-y-2 font-times text-xl md:text-lg">
          {SOCIALS.map(([name, label]) => (
            <li key={name}>
              <a
                href={LINKS[name]}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 py-2 text-bone/75 md:py-0 transition-colors hover:text-mustard"
              >
                {label} <ArrowUpRight />
              </a>
            </li>
          ))}
        </ul>
      </div>
    </main>
  );
}
